import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverPortal } from "@radix-ui/react-popover"
import { type IObservableValue, runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import {
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { BranchSelector } from "@/components/BranchSelection/BranchSelector.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { SplitButtonContainer } from "@/components/Button/SplitButton.tsx"
import { Attachments } from "@/components/Chat/Attachments.tsx"
import { ContextItemPill, ContextItemPillRemove } from "@/components/Chat/ContextItemPill.tsx"
import { DomElementScreenshotTooltip } from "@/components/Chat/DomElementScreenshot.tsx"
import { UserToUserChatNotificationPane } from "@/components/Chat/UserToUserChatNotificationPane.tsx"
import { CircularProgress } from "@/components/CircularProgress/CircularProgress.tsx"
import { CodingGuidelinesDialog } from "@/components/CodingGuidelinesDialog/CodingGuidelinesDialog.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { FigmaConnectDialog } from "@/components/Figma/FigmaConnectDialog.tsx"
import { FigmaInputPopup } from "@/components/Figma/FigmaInputPopup.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { LLMSelector } from "@/components/LLMSelector/LLMSelector.tsx"
import { AddMCPServerDialog } from "@/components/MCP/AddMCPServerDialog.tsx"
import { ProjectSettingsDialog } from "@/components/ProjectSettingsDialog/ProjectSettingsDialog.tsx"
import { type ProjectSettingsTabId, TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { AddContext } from "@/components/PromptInput/AddContext.tsx"
import { ButtonSend } from "@/components/PromptInput/ButtonSend.tsx"
import { CompletionDropdown } from "@/components/PromptInput/Completion/CompletionDropdown.tsx"
import { PromptInputStyles } from "@/components/PromptInput/PromptInputStyles.ts"
import { PromptTextAreaProseMirror } from "@/components/PromptInput/PromptTextAreaProseMirror.tsx"
import {
  Input_Bordered,
  Input_Borderless,
  Input_SingleLine,
  Input_WithLockIcon,
  LockIconInside,
  ToggleSecret,
  ToggleSecret_Offset
} from "@/components/PromptInput/SecretField.ts"
import { ResizeableTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { useScrollableModel } from "@/components/Scrollable/ScrollableContext.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { PillToggleGroup } from "@/components/ToggleGroup/PillToggleGroup.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useInvalidateQueries, useQueries } from "@/hooks/useRootContext.ts"
import {
  CloseLight,
  DeviceRegular,
  FigmaColor,
  FigmaRegular,
  HandWaveRegular,
  Icon16,
  Icon20,
  MentionRegular,
  MoreRegular,
  PickerRegular,
  RulesRegular
} from "@/icons/Icons.ts"
import { getSortedFiles, SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { useCurrentProjectOrNull, useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { pre } from "@/styles/textStyles.ts"

import type {
  InviteDeveloperHint,
  PromptInputMode,
  PromptInputModel,
  PromptInputModeType
} from "@/components/PromptInput/PromptInputModel.ts"
import type { AiAgentTask, ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskContextLoadModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { Property } from "csstype"


const getStorageKeyForHint = (hint: InviteDeveloperHint): string => {
  return `invite-developer-popover-${hint.id}`
}

export const PromptInput = observer((props: {
  model: PromptInputModel
  task?: AiAgentTask | null
  stopAction?: () => void
  talkingToOtherUsers?: string[] | null
  placeholder?: string
  singleLine?: boolean
  projectKey?: ProjectKey
  repositoryName?: string
  contextLoadModel?: TaskContextLoadModel
}) => {
  const devMode = useDevMode()

  const model = props.model
  const inputMode = model.selectedInputMode.get()
  const branchSelection = model.branchSelection.get()
  const llmSelection = model.llmSelection.get()

  const isDragging = model.isDragging.get()
  const [isShaking, setIsShaking] = useState(false)

  const [guidelinesDialogOpen, setGuidelinesDialogOpen] = useState(false)

  const [figmaPopupOpen, setFigmaPopupOpen] = useState(false)
  const [figmaDialogOpen, setFigmaDialogOpen] = useState(false)

  const [mcpServerDialogOpen, setMcpServerDialogOpen] = useState(false)

  const currentProject = useCurrentProjectOrNull()
  const permissions = useOrganizationPermissions()

  const isReadonly = model.readOnly.get()
  const isDebugEvent = model.liveUpdateDebugEvents.get()
  const canInviteDeveloper = permissions.canInviteUsers && model.supportedInputModes.get().some(it => it.type === "InviteDeveloper")
  const canRequestSupport = model.supportedInputModes.get().some(it => it.type === "RequestSupport")

  const stopActionRef = useRef(props.stopAction)
  stopActionRef.current = props.stopAction

  const maybeDoSubmit = useCallback(() => {
    if (stopActionRef.current) {
      setIsShaking(true)
      setTimeout(() => setIsShaking(false), 300)
    } else {
      void model.doSubmit()
    }
  }, [model])

  useHotkeys("Enter", (e) => {
    e.preventDefault()
    maybeDoSubmit()
  }, {
    enabled: (e) => {
      if (devMode) return false

      const readyToSubmit = model.canSubmit.get() && !model.completionModel.isVisible.get() && model.uploadComplete.get()
      const activeElement = document.activeElement
      const inputOrBodyActive = activeElement === document.body
        || activeElement === model.inputRef.current
        || activeElement === model.tokenInputRef.current
      const newLineRequested = e.shiftKey
      return readyToSubmit && inputOrBodyActive && !newLineRequested
    },
    enableOnFormTags: true
  })

  const showFigmaWarning = model.showFigmaWarning.get()
  const hasFigmaAuth = model.figmaStatus.get()?.isConnected == true
  useEffect(() => {
    setFigmaPopupOpen(showFigmaWarning && !hasFigmaAuth)
  }, [showFigmaWarning, hasFigmaAuth])

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [settingsInitialTab, setSettingsInitialTab] = useState<ProjectSettingsTabId | undefined>(undefined)

  const isInviteOrSupportMode = new Set<PromptInputModeType>(["InviteDeveloper", "RequestSupport"])
    .has(model.selectedInputMode.get().type)

  const currentMode = model.selectedInputMode.get()
  const promptInputModes = model.supportedInputModes.get()
  const supportedModes = useMemo(
    () => promptInputModes.filter(item => !excludedTypes.has(item.type)),
    [promptInputModes]
  )

  const hasInputModeToggle = supportedModes.length > 1 && !isInviteOrSupportMode
  const hasInviteDeveloperButton = (canInviteDeveloper || canRequestSupport) && !isInviteOrSupportMode
  const alignFooterItems: Property.AlignItems = hasInputModeToggle || hasInviteDeveloperButton || isInviteOrSupportMode ? "center" : "flex-end"

  const firstInputOnChange = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => model.onInputChanged(event, 0), [model])
  const firstInputOnSelect = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => model.onSelect(event, 0), [model])

  const secondInputOnChange = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => model.onInputChanged(event, 1), [model])
  const secondInputOnSelect = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => model.onSelect(event, 1), [model])

  return (
    <Container
      ref={model.containerRef}
      onClick={() => model.inputRef.current?.focus()}
      className={cx(
        PromptInputStyles.Container,
        isDragging ? PromptInputStyles.Highlighted : null,
        isReadonly ? PromptInputStyles.Disabled : null,
        isShaking ? PromptInputStyles.Shaking : null,
        isDebugEvent && !isDragging && !isShaking ? PromptInputStyles.Pulsing : null,
        props.singleLine ? PromptInputStyles.SingleLine : null
      )}
      onSubmit={model.onSubmit}
      onDragOver={model.handleDragOver}
      onDragLeave={model.handleDragLeave}
      onDrop={model.handleDrop}
    >
      {props.task?.chatMode == "UserToUser"
        && props.talkingToOtherUsers
        && model.selectedInputMode.get().type == "Chat" && (
          <UserToUserChatNotificationPane talkingToOtherUsers={props.talkingToOtherUsers} />
        )}

      <CompletionDropdown model={model.completionModel} />

      <SecretInput
        model={model}
        value={model.firstInputValue}
        placeholder={props.placeholder ?? inputMode.firstInputPlaceholder}
        onChange={firstInputOnChange}
        onSelect={firstInputOnSelect}
        onSubmit={maybeDoSubmit}
        isSecret={inputMode.isFirstInputSecret}
        isFirstInput={true}
        singleLine={props.singleLine}
        ref={inputMode.hasSecondInput ? undefined : model.inputRef}
      />

      {inputMode.hasSecondInput && (
        <SecretInput
          model={model}
          value={model.secondInputValue}
          placeholder={inputMode.secondInputPlaceholder}
          onChange={secondInputOnChange}
          onSelect={secondInputOnSelect}
          isSecret={inputMode.isSecondInputSecret}
          isFirstInput={false}
          singleLine={props.singleLine}
        />
      )}

      <PromptInputAttachments model={model} />

      <Footer alignItems={alignFooterItems} className={cx(props.singleLine ? Footer_SingleLine : null)}>
        {hasInputModeToggle && (
          <InputModeToggle currentMode={currentMode} supportedModes={supportedModes} model={props.model} />
        )}

        {inputMode.extensions.attachments && <AddContext model={model} />}

        {inputMode.extensions.mentions && (
          <Tooltip content="Add mention">
            <PromptInputStyles.FooterIconContainer
              tabIndex={0}
              onClick={model.addMention}
              onKeyDown={(e) => {
                if (e.key === "Enter") model.addMention()
              }}
              data-testid="add-mention-button"
            >
              <MentionRegular className={Icon20} />
            </PromptInputStyles.FooterIconContainer>
          </Tooltip>
        )}

        {inputMode.extensions.figma && (
          <>
            {figmaPopupOpen
              ? <FigmaColor />
              : (
                <Tooltip content={figmaPopupOpen ? undefined : "Import from Figma"}>
                  <FigmaToggle
                    tabIndex={0}
                    onClick={() => {
                      if (hasFigmaAuth) {
                        setFigmaPopupOpen(true)
                      } else {
                        setFigmaDialogOpen(true)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (hasFigmaAuth) {
                          setFigmaPopupOpen(true)
                        } else {
                          setFigmaDialogOpen(true)
                        }
                      }
                    }}
                    data-testid="import-from-figma-button"
                  >
                    <FigmaRegular className={Icon20} />
                    <FigmaColor className={Icon20} />
                  </FigmaToggle>
                </Tooltip>
              )
            }

            <FigmaInputPopup
              isOpen={figmaPopupOpen}
              onOpenChange={setFigmaPopupOpen}
              hasAuth={hasFigmaAuth}
              connectToFigma={model.connectToFigma}
            />

            <FigmaConnectDialog
              isOpen={figmaDialogOpen}
              onOpenChange={setFigmaDialogOpen}
              connectToFigma={model.connectToFigma}
            />
          </>
        )}

        {inputMode.extensions.guidelines && (
          <Tooltip content="Coding guidelines">
            <PromptInputStyles.FooterIconContainer
              tabIndex={0}
              onClick={() => setGuidelinesDialogOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setGuidelinesDialogOpen(true)
              }}
              data-testid="coding-guidelines-button"
            >
              <RulesRegular className={Icon20} />
            </PromptInputStyles.FooterIconContainer>
          </Tooltip>
        )}

        {devMode && inputMode.extensions.mcp && (
          <>
            <Tooltip content="Connect remote MCP">
              <PromptInputStyles.FooterIconContainer
                tabIndex={0}
                onClick={() => setMcpServerDialogOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setMcpServerDialogOpen(true)
                  }
                }}
                data-testid="add-mcp-server-button"
              >
                <DeviceRegular className={Icon20} />
              </PromptInputStyles.FooterIconContainer>
            </Tooltip>

            <AddMCPServerDialog
              isOpen={mcpServerDialogOpen}
              onOpenChange={setMcpServerDialogOpen}
              onAddServer={model.installRemoteMCP}
            />
          </>
        )}

        {inputMode.extensions.llmSelector && (
          <LLMSelector llmSelection={llmSelection} devMode={devMode} />
        )}

        {inputMode.extensions.branchSelector && (
          <BranchSelector
            model={branchSelection}
            inputModel={model}
            projectKey={props.projectKey}
            repositoryName={props.repositoryName}
            goToSettings={() => {
              setSettingsInitialTab(TabId.Git)
              setSettingsDialogOpen(true)
            }}
          />
        )}

        {props.contextLoadModel && inputMode.type === "Chat" && devMode && (
          <ContextLoadIndicator contextLoadModel={props.contextLoadModel} />
        )}

        {!props.singleLine && <Spring />}

        {hasInviteDeveloperButton && (
          <InviteSplitButton
            model={props.model}
            disabled={props.stopAction != null || model.isSubmitting.get() || !model.uploadComplete.get()}
          />
        )}

        {isInviteOrSupportMode && (
          <CancelInviteButton
            kind="alternative"
            disabled={model.isSubmitting.get()}
            onClick={() => model.setInputMode(model.previousInputMode?.type ?? model.supportedInputModes.get()[0].type)}
          >
            Cancel
          </CancelInviteButton>
        )}

        <ButtonSend
          mode={inputMode.type}
          disabled={!model.canSubmit.get() || !model.uploadComplete.get() || model.isSubmitting.get()}
          canSubmit={model.canSubmit.get()}
          stopAction={props.stopAction}
          data-testid="send-idea-button"
        />
      </Footer>

      {inputMode.extensions.guidelines && currentProject && (
        <CodingGuidelinesDialog
          project={currentProject}
          open={guidelinesDialogOpen}
          onOpenChange={setGuidelinesDialogOpen}
        />
      )}

      {currentProject && (
        <ProjectSettingsDialog
          project={currentProject}
          dialogOpen={settingsDialogOpen}
          setDialogOpen={(open) => {
            setSettingsDialogOpen(open)
            if (!open) setSettingsInitialTab(undefined)
          }}
          initialTab={settingsInitialTab}
        />
      )}
    </Container>
  )
})


export const PromptInputAttachments = observer(({ model }: { model: PromptInputModel }) => {
  const context = model.context.get()
  const sortedContextItems = useMemo(() => getSortedFiles(context), [context])

  return (
    <>
      {(context.length > 0 || model.attachments.get().length > 0 || model.elements.get().length > 0) && (
        <AttachmentsScrollable>
          <Attachments
            attachments={model.resolvedAttachments.get()}
            contextItems={sortedContextItems}
            domElements={model.elements.get()}
            domElementBuilder={element => {
              if (!element) return null
              return (
                <DomElementScreenshotTooltip key={element.id} align="start" content={
                  element.screenshotPngBase64
                    ? <Screenshot src={element.screenshotPngBase64} alt="Element screenshot" />
                    : <Path>{element.path}</Path>
                }>
                  <ContextItemPill withRemoveIcon>
                    <PickerRegular className={Icon16} />
                    <EllipsisContainer>
                      <Ellipsis>
                        {element.name}
                      </Ellipsis>
                    </EllipsisContainer>
                    <a href="#" onClick={(e) => {
                      e.preventDefault()
                      model.removeDomAttachment(element)
                    }}>
                      <Tooltip content="Remove"><ContextItemPillRemove /></Tooltip>
                    </a>
                  </ContextItemPill>
                </DomElementScreenshotTooltip>
              )
            }}
            showPreviewTooltips={false}
            allowDownload={false}
            onDownload={(file) => model.downloadAttachment(file)}
            onRemoveAttachment={async (file) => {
              const currentAttachments = model.attachments.get()

              const resolvedAttachments = (await Promise.allSettled(currentAttachments)).map((result) => ({
                status: result.status,
                value: result.status === "fulfilled" ? result.value : null
              }))

              const filteredAttachments = currentAttachments.filter((_, index) => {
                const resolved = resolvedAttachments[index]
                return resolved.status === "fulfilled" && resolved.value!.id !== file.id
              })

              runInAction(() => {
                model.attachments.set(filteredAttachments)
              })
            }}
            onRemoveFailedAttachment={(index) => {
              runInAction(() => {
                model.attachments.set(model.attachments.get().filter((_, i) => i !== index))
              })
            }}
            onRemoveContextItem={(item) => {
              runInAction(() => {
                const filteredItems = context.filter(thisItem => thisItem.name !== item.name)
                model.context.set(filteredItems)
              })
            }}
          />
        </AttachmentsScrollable>
      )}
    </>
  )
})

export const PromptTextArea = observer(({ model, isModified, placeholder, onCancel, onSubmit }: {
  model: PromptInputModel
  isModified: boolean
  placeholder: string
  onCancel: () => void
  onSubmit: () => void
}) => {
  const devMode = useDevMode()

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (model.completionModel.isVisible.get()) {
      model.onKeyDown(event)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      onCancel()
      return
    }

    if (event.key === "Enter" && !event.shiftKey && model.canSubmit.get() && isModified) {
      event.preventDefault()
      onSubmit()
    }
  }, [model, isModified, onCancel, onSubmit])

  useEffect(() => {
    if (devMode) return
    const textarea = model.inputRef.current
    if (model.autoFocus.get() && textarea) {
      const textLength = textarea.value.length
      textarea.setSelectionRange(textLength, textLength)
    }
  }, [devMode, model])

  if (devMode) {
    return (
      <PromptTextAreaProseMirror
        model={model}
        value={model.firstInputValue}
        placeholder={placeholder}
        className={cx(PromptInputStyles.Input, Input_Borderless)}
        autoFocus={!model.selectedInputMode.get().hasSecondInput && model.autoFocus.get()}
        readOnly={model.readOnly.get()}
        richText={true}
        testId="chat-input"
        onCancel={onCancel}
        onSubmit={onSubmit}
        ref={model.inputRef}
      />
    )
  }

  return (
    <ResizeableTextarea
      ref={model.inputRef}
      rows={1}
      value={model.firstInputValue.get()}
      className={cx(PromptInputStyles.Input, Input_Borderless)}
      placeholder={placeholder}
      onChange={(event) => model.onInputChanged(event, 0)}
      onKeyDown={handleKeyDown}
      onSelect={(event: ChangeEvent<HTMLTextAreaElement>) => model.onSelect(event, 0)}
      onPaste={model.handlePaste}
      onFocus={model.onFocus}
      onBlur={model.onBlur}
      autoFocus={!model.selectedInputMode.get().hasSecondInput && model.autoFocus.get()}
      readOnly={model.readOnly.get()}
      data-testid="chat-input"
    />
  )
})

const SecretInput = observer((props: {
  model: PromptInputModel
  value: IObservableValue<string>
  placeholder: string
  onChange: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  onSelect: (event: ChangeEvent<HTMLTextAreaElement>) => void
  onSubmit?: () => void
  isSecret: boolean
  isFirstInput: boolean
  singleLine?: boolean
  ref?: RefObject<HTMLTextAreaElement | null>
}) => {
  const model = props.model
  const inputMode = props.model.selectedInputMode.get()

  const devMode = useDevMode()

  const [showSecret, setShowSecret] = useState(false)

  if (props.isSecret) {
    const showLockIcon = !props.isFirstInput

    return (
      <SecretInputContainer>
        <input
          ref={model.tokenInputRef}
          className={cx(
            PromptInputStyles.Input,
            inputMode.hasSecondInput ? Input_Bordered : Input_Borderless,
            props.singleLine ? Input_SingleLine : null,
            showLockIcon ? Input_WithLockIcon : null
          )}
          value={props.value.get()}
          placeholder={props.placeholder}
          type={showSecret ? "text" : "password"}
          onChange={props.onChange}
          onKeyDown={model.onKeyDown}
          disabled={props.model.readOnly.get()}
        />

        {showLockIcon && <LockIconInside />}

        <ToggleSecret
          size="small"
          kind="alternative"
          disabled={props.model.readOnly.get() || props.value.get().length === 0 || props.value.get() === SECRET_VALUE_PLACEHOLDER}
          className={cx(inputMode.hasSecondInput ? ToggleSecret_Offset : null)}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowSecret(!showSecret)
          }}
        >
          {showSecret ? "Hide" : "Show"}
        </ToggleSecret>
      </SecretInputContainer>
    )
  } else {
    if (devMode) {
      return (
        <PromptTextAreaProseMirror
          model={model}
          value={props.value}
          placeholder={props.placeholder}
          className={cx(
            PromptInputStyles.Input,
            inputMode.hasSecondInput ? Input_Bordered : Input_Borderless,
            props.singleLine ? Input_SingleLine : null
          )}
          autoFocus={!inputMode.hasSecondInput && model.autoFocus.get()}
          readOnly={props.model.readOnly.get()}
          richText={!props.singleLine}
          testId="chat-input"
          onChange={props.onChange}
          onSubmit={props.onSubmit}
          ref={props.ref}
        />
      )
    } else {
      return (
        <ResizeableTextarea
          ref={props.ref}
          rows={1}
          value={props.value.get()}
          className={cx(
            PromptInputStyles.Input,
            inputMode.hasSecondInput ? Input_Bordered : Input_Borderless,
            props.singleLine ? Input_SingleLine : null
          )}
          placeholder={props.placeholder}
          onChange={props.onChange}
          onKeyDown={model.onKeyDown}
          onSelect={props.onSelect}
          onPaste={model.handlePaste}
          onFocus={model.onFocus}
          onBlur={model.onBlur}
          autoFocus={!inputMode.hasSecondInput && model.autoFocus.get()}
          readOnly={props.model.readOnly.get()}
          data-testid="chat-input"
        />
      )
    }
  }
})

const ContextLoadIndicator = observer(({ contextLoadModel }: {
  contextLoadModel: TaskContextLoadModel
}) => {
  const contextLoad = contextLoadModel.contextLoad.get()
  const currentSize = contextLoadModel.currentContextSize.get()
  const totalLimit = contextLoadModel.totalContextLimit.get()

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`
    }
    return num.toString()
  }

  const tooltipContent = totalLimit !== null ? (
    <Vertical gap="4px">
      <div>Context tokens: {formatNumber(currentSize)} / {formatNumber(totalLimit)}</div>
      {contextLoad !== null && (
        <div>Usage: {(contextLoad * 100).toFixed(1)}%</div>
      )}
    </Vertical>
  ) : (
    <div>Context tokens: {formatNumber(currentSize)}</div>
  )

  return (
    <Tooltip content={tooltipContent}>
      <CircularProgressContainer>
        <CircularProgress
          value={contextLoad}
          size={20}
          strokeWidth={2}
        />
      </CircularProgressContainer>
    </Tooltip>
  )
})

const excludedTypes = new Set<PromptInputModeType>(["InviteDeveloper", "RequestSupport"] as const)

const InputModeToggle = observer(({ currentMode, supportedModes, model }: {
  currentMode: PromptInputMode
  supportedModes: PromptInputMode[]
  model: PromptInputModel
}) => {
  const scrollableContext = useScrollableModel()

  return (
    <PillToggleGroup
      items={supportedModes}
      selectedItem={currentMode}
      onItemChange={(mode) => {
        model.setInputMode(mode.type)
        requestAnimationFrame(() => {
          scrollableContext.scrollToBottom()
        })
      }}
      getKey={(mode) => mode.type}
      getValue={(mode) => mode.name}
    />
  )
})

const InviteSplitButton = observer(({ model, disabled }: { model: PromptInputModel, disabled: boolean }) => {
  const queries = useQueries()
  const invalidateQueries = useInvalidateQueries()

  const [inviteDeveloperPopupOpen, setInviteDeveloperPopupOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const inviteDeveloperHint = model.inviteDeveloperHint.get()
  const showInviteDeveloperHint = inviteDeveloperHint !== null
  const hintPersistenceKey = inviteDeveloperHint ? getStorageKeyForHint(inviteDeveloperHint) : null

  useEffect(() => {
    if (showInviteDeveloperHint && hintPersistenceKey) {
      void queries.getProfilePreference(hintPersistenceKey).then(
        (preference) => {
          if (preference === null || preference !== "false" && !disabled) {
            setInviteDeveloperPopupOpen(true)
          }
        }
      )
    } else {
      setInviteDeveloperPopupOpen(false)
    }
  }, [disabled, hintPersistenceKey, queries, showInviteDeveloperHint])

  const closeHint = useCallback(() => {
    setInviteDeveloperPopupOpen(false)
    if (hintPersistenceKey) {
      void queries.apiClient.setProfilePreference(hintPersistenceKey, "false")
      void invalidateQueries({ queryKey: ["getProfilePreference", hintPersistenceKey] })
    }
  }, [hintPersistenceKey, invalidateQueries, queries.apiClient])

  return (
    <SplitButtonContainer>
      <InviteDeveloperButton
        kind="alternative"
        size="large"
        disabled={disabled}
        onClick={() => {
          model.setInputMode("InviteDeveloper")
        }}
      >
        <EllipsisContainer><Ellipsis>Invite developer</Ellipsis></EllipsisContainer>
      </InviteDeveloperButton>

      {inviteDeveloperHint && (
        <Popover open={inviteDeveloperPopupOpen}>
          <InviteDeveloperPopoverAnchor />
          <PopoverPortal>
            <InviteDeveloperPopoverContent onOpenAutoFocus={(e) => e.preventDefault()} side="top">
              <InviteDeveloperPopoverClose asChild>
                <CloseLight className={Icon16} onClick={closeHint} />
              </InviteDeveloperPopoverClose>

              <Vertical alignItems="flex-end" gap="8px">
                <Vertical alignItems="flex-start">
                  <InviteDeveloperPopoverTitle>{inviteDeveloperHint.title}</InviteDeveloperPopoverTitle>

                  <span>{inviteDeveloperHint.callToAction}</span>
                </Vertical>

                <SplitButtonContainer>
                  <InviteDeveloperButtonInsidePopover kind="alternative" size="large" onClick={() => {
                    model.setInputMode("InviteDeveloper")
                    closeHint()
                  }}>
                    <EllipsisContainer><Ellipsis>Invite developer</Ellipsis></EllipsisContainer>
                  </InviteDeveloperButtonInsidePopover>
                  <MoreButtonInsidePopover
                    size="large"
                    kind="alternative"
                    onClick={() => {
                      model.setInputMode("RequestSupport")
                      closeHint()
                    }}
                  >
                    <MoreRegular className={Icon16} />
                  </MoreButtonInsidePopover>
                </SplitButtonContainer>
              </Vertical>
            </InviteDeveloperPopoverContent>
          </PopoverPortal>
        </Popover>
      )}

      {/* "More" menu */}

      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <MoreButton
            size="large"
            kind="alternative"
            disabled={disabled}
          >
            <MoreRegular className={Icon16} />
          </MoreButton>
        </DropdownMenuTrigger>

        <StyledDropdownMenuContent align="end">
          <StyledDropdownMenuItem
            onClick={() => {
              model.setInputMode("RequestSupport")
            }}
          >
            <Horizontal gap="8px">
              <HandWaveRegular />
              <Vertical alignItems="flex-start">
                <RequestSupportTitle>Request support</RequestSupportTitle>
                <StyledSecondaryTextSpan>
                  Need help? Send a request to our developers, and we'll respond within 72 hours.
                </StyledSecondaryTextSpan>
              </Vertical>
            </Horizontal>
          </StyledDropdownMenuItem>
        </StyledDropdownMenuContent>
      </DropdownMenu>

    </SplitButtonContainer>
  )
})

const MoreButton = styled(Button)`
  padding-left: 8px;
  padding-right: 12px;
`

const MoreButtonInsidePopover = styled(MoreButton)`
  &:hover:not(:disabled) {
    background: white !important;
  }
`

const StyledDropdownMenuContent = styled(DropdownMenuContent)`
  min-width: 120px;
`

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  display: flex;
  cursor: pointer;
  width: 340px;
`

const StyledSecondaryTextSpan = styled(SecondaryTextSpan)`
  line-height: 18px;
  font-size: var(--smaller-font-size);
  text-align: left;
  text-wrap: balance;
`

const RequestSupportTitle = styled.h2`
  font-size: var(--base-font-size);
  font-weight: 500;

  margin: 0 0 4px;

  color: var(--text-color);
`

const Container = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  position: relative;

  width: 100%;
  max-width: var(--chat-width);
  padding: 16px;
`

const SecretInputContainer = styled(Horizontal)`
  position: relative;
`

const AttachmentsScrollable = styled(Scrollable)`
  max-height: 40vh;
`

const Footer = styled(Horizontal)`
  position: relative;

  flex-wrap: wrap;
  gap: 12px;

  min-height: 40px;
`

const Footer_SingleLine = css`
  flex-wrap: nowrap !important;

  position: absolute !important;
  bottom: 12px;
  right: 12px;
`

const CancelInviteButton = styled(Button)`
  border: none;
  background: var(--text-color-5);

  &:hover:not(:disabled) {
    background: var(--text-color-10);
  }
`

const FigmaToggle = styled(PromptInputStyles.FooterIconContainer)`
  position: relative;

  .svg-icon {
    position: absolute;
    top: 2px;
    left: 2px;
    transition: opacity 0.2s ease-out, transform 0.1s ease-out;
  }

  .svg-icon:first-child {
    opacity: 1;
  }

  .svg-icon:last-child {
    opacity: 0;
  }

  &:hover .svg-icon:first-child {
    opacity: 0;
  }

  &:hover .svg-icon:last-child {
    opacity: 1;
    transform: scale(1.05);
  }

  &:active .svg-icon:last-child {
    transform: scale(0.95);
  }
`

const Path = styled.pre`
  ${pre};

  max-width: 50vw;
`

const Screenshot = styled.img`
  display: block;
  max-width: 50vw;
  max-height: 50vh;
`

const InviteDeveloperButton = styled(Button)`
  padding-right: 12px;
`

const InviteDeveloperButtonInsidePopover = styled(InviteDeveloperButton)`
  &:hover:not(:disabled) {
    background: white !important;
  }
`

const InviteDeveloperPopoverAnchor = styled(PopoverAnchor)`
  position: absolute;
  bottom: -13px;
  right: 200px;
`

const InviteDeveloperPopoverContent = styled(PopoverContent)`
  width: 300px;
  padding: 12px 16px;

  border: 1px solid rgb(194 101 173 / 20%);
  border-radius: var(--border-radius-24);

  color: white;
  background: radial-gradient(158.68% 172.73% at 54.2% 100%, #C265AD 0%, #8A5DE3 100%), linear-gradient(89deg, #C265AD 8.47%, #7A7AEF 92.69%), #F8E8FE;
  box-shadow: 0 2px 10px 0 rgb(26 46 160 / 10%);

  &[data-state="open"] {
    animation: fade-in 250ms ease-out, slide-up 150ms ease-out;
  }

  &[data-state="closed"] {
    animation: fade-out 250ms ease-in;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes fade-out {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }

  @keyframes slide-up {
    from {
      transform: translateY(32px);
    }

    to {
      transform: translateY(0);
    }
  }
`

const InviteDeveloperPopoverTitle = styled.span`
  font-weight: 600;
`

const InviteDeveloperPopoverClose = styled(PopoverClose)`
  position: absolute;
  right: 8px;
  top: 8px;

  cursor: pointer;
`

const CircularProgressContainer = styled(Horizontal)`
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;
`